import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { watchJobs, getCompany } from "../../services/firestore";
import "./OwnerLayout.css";

const navItems = [
  { section: "Operations" },
  { to: "/owner/dashboard", label: "Dashboard", icon: "🏠" },
  { to: "/owner/inbox", label: "Job inbox", icon: "📋", countKey: "inbox" },
  { to: "/owner/intake", label: "Email intake", icon: "📨" },
  { to: "/owner/live", label: "Live jobs", icon: "⏱", countKey: "live" },
  { to: "/owner/billing", label: "Ready to bill", icon: "✅", countKey: "billing" },
  { section: "Billing" },
  { to: "/owner/invoices", label: "Invoices", icon: "🧾" },
  { to: "/owner/financials", label: "Financials", icon: "💰" },
  { section: "Directory" },
  { to: "/owner/locations", label: "Locations", icon: "📍" },
  { to: "/owner/reps", label: "Account reps", icon: "🤝" },
  { to: "/owner/guards", label: "Guards", icon: "👥" },
  { section: "Config" },
  { to: "/owner/rates", label: "Rate cards", icon: "💲" },
  { to: "/owner/settings", label: "Settings", icon: "⚙️" },
];

export default function OwnerLayout() {
  const { userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [counts, setCounts] = useState({ inbox: 0, live: 0, billing: 0 });
  // Pulls the company display name from the company doc so the sidebar
  // brand line always reads correctly without per-deploy code edits.
  const [companyName, setCompanyName] = useState("");
  // Mobile sidebar drawer state. Closed by default on mobile; the
  // hamburger toggles it. Always visible on desktop (CSS handles the
  // breakpoint, the state just gets ignored at >900px).
  const [sidebarOpen, setSidebarOpen] = useState(false);
  useEffect(() => {
    if (!userProfile?.companyId) return;
    getCompany(userProfile.companyId)
      .then((c) => setCompanyName(c?.name || c?.displayName || ""))
      .catch(() => {});
  }, [userProfile?.companyId]);

  // Live counts driven by the same job-watch the pages use, so the sidebar
  // stays in sync without a separate query.
  useEffect(() => {
    if (!userProfile?.companyId) return;
    // Pass null for status filter → load every job; we bucket client-side.
    const unsub = watchJobs(userProfile.companyId, null, (jobs) => {
      const inboxStatuses = new Set(["intake","pool","grabbed","confirmed","inProgress"]);
      const inbox = jobs.filter((j) => inboxStatuses.has(j.status)).length;
      const live = jobs.filter((j) => j.status === "inProgress").length;
      const today = new Date();
      const ymd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      const bundleHints = ["dai", "dana augustine", "augustine"];
      // Is this a DAI-style flat-fee bundled job? (Those are counted via
      // the batch logic below, not per-job.) Everything else — including
      // LALI/EFFY/etc. bundled directs that bill hourly per-job — counts
      // as a normal per-job billable.
      const isDai = (j) => {
        const bt = (j.billingType || "").toLowerCase();
        const bn = (j.brokerName || "").toLowerCase();
        return bt === "bundled" || bundleHints.some((h) => bn.includes(h));
      };
      // Ready-to-bill = completed-and-not-invoiced jobs that aren't DAI
      // (a LALI/EFFY bundled direct is complete + billed per-job, so it
      // counts here) PLUS DAI batches ready to forward (below).
      const completeBillable = jobs.filter((j) =>
        j.status === "complete" && j.invoiceStatus !== "invoiced" && !isDai(j)
      ).length;
      // Bundle billable count: number of DAI batches READY to forward —
      // every job is "ready" (complete OR scheduledDate before today) AND
      // at least one job is uninvoiced. Mirrors ReadyToBill.js bundleList
      // (we only count ready batches; "filling" batches with upcoming
      // jobs don't nag the sidebar since the owner can't act on them).
      const jobReady = (j) =>
        j.status === "complete" ||
        (!!j.scheduledDate && j.scheduledDate.localeCompare(ymd) < 0);
      const byBundle = new Map();
      for (const j of jobs) {
        if (!j.bundleId) continue;
        if (!isDai(j)) continue; // only DAI flat-fee bundles batch here
        const cur = byBundle.get(j.bundleId) || { jobs: [] };
        cur.jobs.push(j);
        byBundle.set(j.bundleId, cur);
      }
      let bundleBillable = 0;
      for (const b of byBundle.values()) {
        if (b.jobs.length === 0) continue;
        if (!b.jobs.every(jobReady)) continue; // still filling → not counted
        if (!b.jobs.some((j) => j.invoiceStatus !== "invoiced")) continue;
        bundleBillable++;
      }
      const billing = completeBillable + bundleBillable;
      setCounts({ inbox, live, billing });
    });
    return unsub;
  }, [userProfile?.companyId]);

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  function handleGuardMode() {
    // Switch to guard mode — navigate to guard app
    navigate("/guard/pool");
  }

  // Auto-close the drawer when a nav item is tapped on mobile —
  // otherwise the user has to manually dismiss after navigating.
  function handleNavClick() {
    setSidebarOpen(false);
  }

  return (
    <div className="owner-shell">
      {/* Mobile-only top bar with hamburger. Hidden at >900px via CSS. */}
      <div className="owner-mobile-topbar">
        <button
          className="owner-hamburger"
          onClick={() => setSidebarOpen((v) => !v)}
          aria-label="Toggle menu"
        >☰</button>
        <div className="owner-mobile-title">VeloceFlow</div>
      </div>

      {/* Backdrop — only visible+clickable on mobile when sidebar is open */}
      <div
        className={`owner-sidebar-backdrop${sidebarOpen ? " open" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />

      <aside className={`owner-sidebar${sidebarOpen ? " open" : ""}`}>
        <div className="sidebar-logo">
          <div className="sidebar-logo-name">VeloceFlow</div>
          {companyName && <div className="sidebar-logo-co">{companyName}</div>}
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item, i) =>
            item.section ? (
              <div key={i} className="sidebar-section">{item.section}</div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={handleNavClick}
                className={({ isActive }) =>
                  `sidebar-nav-item ${isActive ? "active" : ""}`
                }
              >
                <span className="nav-icon">{item.icon}</span>
                <span style={{ flex: 1 }}>{item.label}</span>
                {item.countKey && counts[item.countKey] > 0 && (
                  <span style={{
                    background: "#2E7CD6", color: "#fff",
                    fontSize: 10, fontWeight: 700, lineHeight: 1,
                    padding: "3px 7px", borderRadius: 10, marginLeft: 6,
                  }}>{counts[item.countKey]}</span>
                )}
              </NavLink>
            )
          )}
        </nav>

        <div className="sidebar-guard-mode-btn" onClick={handleGuardMode}>
          <span>⭐</span> Switch to guard mode
        </div>

        <div className="sidebar-footer">
          <div className="sidebar-user-av">
            {userProfile?.name?.charAt(0) || "R"}
          </div>
          <div className="sidebar-user-info">
            <div className="sidebar-user-name">{userProfile?.name || "Owner"}</div>
            <div className="sidebar-user-role">Courier Owner</div>
          </div>
          <button className="sidebar-logout" onClick={handleLogout} title="Sign out">
            ↩
          </button>
        </div>
      </aside>

      <main className="owner-main">
        <Outlet />
      </main>
    </div>
  );
}
